import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useSelector } from "react-redux";
import styled, { keyframes, css } from "styled-components";
import MusicLoader from "../Loader/MusicLoader";
import {
  Image,
  Popconfirm,
  Typography,
  Breadcrumb,
  Button,
  Divider,
  message,
  Tooltip,
  Tag,
  Space,
  notification,
  Modal,
} from "antd";
import { Link } from "react-router-dom";
import AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";
import { saveAs } from "file-saver";

// React Icons
import {
  FaEdit,
  FaTrash,
  FaDownload,
  FaArrowLeft,
  FaPlayCircle,
  FaFileAlt,
  FaCalendarAlt,
  FaInfoCircle,
  FaTag,
  FaCloudDownloadAlt,
  FaLink,
  FaUsers,
  FaUser,
  FaShieldAlt,
  FaApple,
  FaFacebook,
  FaInstagram,
  FaGlobe,
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
  FaTimesCircle,
  FaMusic,
  FaCopy,
  FaSpotify,
} from "react-icons/fa";

import {
  MdCheckCircleOutline,
  MdAccessTime,
  MdSync,
  MdCancel,
  MdErrorOutline,
  MdInfo,
  MdContentCopy,
  MdMusicNote,
  MdImage,
} from "react-icons/md";

const { Title, Text, Paragraph } = Typography;

// Animation keyframes
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// Page container
const PageContainer = styled.div`
  width: 100%;
  height: 99%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  animation: ${fadeIn} 0.5s ease;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f0f2f5;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 10px;

    &:hover {
      background: #bfbfbf;
    }
  }

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 600;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const BreadcrumbContainer = styled.div`
  margin-bottom: 8px;
`;

const NavigationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

// Main content card
const ContentCard = styled.div`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  animation: ${slideUp} 0.6s ease;
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  min-height: 600px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    min-height: auto;
    overflow-y: scroll;
  }
`;

// Album section (left side)
const AlbumSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, #f9f9fa 0%, #f1f2f6 100%);
  border-right: 1px solid #f0f0f0;
  padding: 40px 30px;
  gap: 24px;

  @media (max-width: 1024px) {
    border-right: none;
    border-bottom: 1px solid #f0f0f0;
    padding: 20px;
    gap: 6px;
  }
`;

const AlbumCover = styled.div`
  width: 100%;
  max-width: 300px;
  display: flex;
  justify-content: center;
  margin-bottom: 8px;

  img {
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.02);
      box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
    }
  }
`;

const AlbumTitle = styled(Title)`
  text-align: center !important;
  margin-top: 8px !important;
  margin-bottom: 8px !important;
  background: linear-gradient(90deg, #222, #666);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const AlbumDescription = styled(Paragraph)`
  text-align: center;
  color: rgba(0, 0, 0, 0.65);
  max-width: 320px;
  line-height: 1.6;
  font-size: 15px;
  word-break: break-word;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
`;

const DescriptionButton = styled(Button)`
  padding: 0;
  height: auto;
  font-weight: 500;
  margin-top: -2rem;
`;

const FullDescription = styled(Paragraph)`
  max-height: 60vh;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.7;
  margin-bottom: 0 !important;
`;

const StyledAudioPlayer = styled.div`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 16px;

  .rhap_container {
    background-color: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    padding: 12px;
  }

  .rhap_main-controls-button {
    color: #1677ff;
  }

  .rhap_progress-indicator {
    background: #1677ff;
  }

  .rhap_progress-filled {
    background: #1677ff;
  }

  .rhap_download-progress {
    background: #e6f4ff;
  }

  .rhap_time {
    color: rgba(0, 0, 0, 0.65);
  }

  .rhap_play-pause-button {
    font-size: 40px;
    width: 40px;
    height: 40px;
  }
`;

// Details section (right side)
const DetailsSection = styled.div`
  padding: 40px 30px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  @media (max-width: 1024px) {
    padding: 20px 20px;
    overflow: hidden;
    height: fit-content;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f9f9f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 3px;

    &:hover {
      background: #bfbfbf;
    }
  }
`;

const SectionTitle = styled(Title)`
  margin-bottom: 24px !important;
  position: relative;
  padding-bottom: 8px;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #1677ff, #69c0ff);
    border-radius: 3px;
  }
`;

const DetailsGroup = styled.div`
  margin-bottom: 28px;
`;

const GroupTitle = styled(Title)`
  font-size: 16px !important;
  margin-bottom: 12px !important;
  color: #555;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DetailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 40% 60%;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #f0f0f0;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
    border-color: #e6f7ff;
  }

  ${(props) =>
    props.highlighted &&
    css`
      background-color: #f0f7ff;
      border-color: #bae0ff;

      &:hover {
        box-shadow: 0 2px 12px rgba(24, 144, 255, 0.1);
      }
    `}

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailLabel = styled.div`
  padding: 12px 16px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  background-color: #fafafa;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: rgba(0, 0, 0, 0.45);
    font-size: 16px;
  }

  @media (max-width: 768px) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const DetailValue = styled.div`
  padding: 12px 16px;
  color: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: space-between;
  word-break: break-word;

  a {
    color: #1677ff;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;

    &:hover {
      color: #4096ff;
      text-decoration: underline;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const ActionButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 180px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const EditButton = styled(ActionButton)`
  background-color: #f0f0f0;
  color: #333;

  &:hover {
    background-color: #e0e0e0;
    color: #111;
  }
`;

const DeleteButton = styled(ActionButton)`
  background-color: #fff1f0;
  color: #ff4d4f;
  border: 1px solid #ffa39e;

  &:hover {
    background-color: #fff1f0;
    color: #cf1322;
    border-color: #ff4d4f;
  }
`;

const TakedownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 180px;
  background-color: #fff7e6;
  color: #fa8c16;
  border: 1px solid #ffd591;
  cursor: pointer;

  &:hover {
    background-color: #fff2e6;
    color: #d4671b;
    border-color: #fa8c16;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #bfbfbf;
    border-color: #d9d9d9;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const StatusTag = styled(Tag)`
  margin-left: 12px;
  padding: 2px 12px;
  font-size: 14px;
  border-radius: 4px;
  text-transform: capitalize;
`;

// Artist tag styling - similar to Form.jsx
const ArtistTag = styled.div`
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #e6f4ff, #f2faff);
  color: black;
  border: 1px solid #c2e4ff;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  margin: 0.25rem 0;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
    border-color: #95d2ff;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-left: 0.75rem;
`;

const SocialIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #0958d9;
  opacity: ${(props) => (props.active ? 1 : 0.4)};
  cursor: ${(props) => (props.active ? "pointer" : "default")};
  transition: all 0.2s;

  &:hover {
    transform: ${(props) => (props.active ? "scale(1.1)" : "none")};
    color: ${(props) => (props.active ? "#0958d9" : "#1677ff")};
  }
`;

// Icons for different field types
const getFieldIcon = (field) => {
  const fieldLower = field.toLowerCase();

  if (fieldLower.includes("title")) return <FaFileAlt />;
  if (fieldLower.includes("date") || fieldLower.includes("time"))
    return <FaCalendarAlt />;
  if (fieldLower.includes("type")) return <FaTag />;
  if (fieldLower.includes("status")) return <FaInfoCircle />;
  if (fieldLower.includes("label")) return <FaUsers />;
  if (fieldLower.includes("singer")) return <FaUser />;
  if (fieldLower.includes("composer")) return <FaMusic />;
  if (fieldLower.includes("lyricist")) return <FaFileAlt />;
  if (fieldLower.includes("director")) return <FaUsers />;
  if (fieldLower.includes("apple")) return <FaApple />;
  if (fieldLower.includes("facebook")) return <FaFacebook />;
  if (fieldLower.includes("instagram")) return <FaInstagram />;
  if (fieldLower.includes("spotify")) return <FaPlayCircle />;
  if (fieldLower.includes("file") || fieldLower.includes("thumbnail"))
    return <FaCloudDownloadAlt />;

  return <FaInfoCircle />;
};

const getStatusInfo = (status) => {
  switch (status) {
    case "completed":
      return {
        icon: <MdCheckCircleOutline size={16} />,
        color: "success",
        text: "Live",
      };
    case "waiting":
      return {
        icon: <MdAccessTime size={16} />,
        color: "warning",
        text: "Awaiting Approval",
      };
    case "processing":
      return {
        icon: <MdSync size={16} />,
        color: "processing",
        text: "Processing",
      };
    case "rejected":
      return {
        icon: <MdCancel size={16} />,
        color: "error",
        text: "Rejected",
      };
    case "takedown":
      return {
        icon: <MdErrorOutline size={16} />,
        color: "error",
        text: "Taken Down",
      };
    default:
      return {
        icon: <MdInfo size={16} />,
        color: "default",
        text: status,
      };
  }
};

// Social platform icons
const getSocialIcon = (platform) => {
  const platformLower = platform.toLowerCase();

  if (platformLower.includes("apple")) return <FaApple />;
  if (platformLower.includes("facebook")) return <FaFacebook />;
  if (platformLower.includes("instagram")) return <FaInstagram />;
  if (platformLower.includes("spotify")) return <FaSpotify />;

  return <FaGlobe />;
};

// Group fields into categories
const groupFields = (fields) => {
  // Initialize groups
  const groups = {
    main: [],
    labels: [],
    dates: [],
    singers: [],
    composers: [],
    lyricists: [],
    other: [],
  };

  fields.forEach((field) => {
    const fieldName = field.field.toLowerCase();

    // Group by category
    if (
      ["description", "status", "albumtype", "songtitle"].includes(fieldName)
    ) {
      groups.main.push(field);
    } else if (fieldName.includes("label")) {
      groups.labels.push(field);
    } else if (fieldName.includes("date") || fieldName.includes("time")) {
      groups.dates.push(field);
    } else if (fieldName.includes("singer")) {
      groups.singers.push(field);
    } else if (fieldName.includes("composer")) {
      groups.composers.push(field);
    } else if (fieldName.includes("lyricist")) {
      groups.lyricists.push(field);
    } else {
      groups.other.push(field);
    }
  });

  return groups;
};

// Function to parse comma-separated artists and their profiles
const parseArtists = (name, appleId, spotifyId, facebookUrl, instagramUrl) => {
  if (!name) return [];

  const names = name.split(",").map((n) => n.trim());
  const appleIds = appleId ? appleId.split(",").map((id) => id.trim()) : [];
  const spotifyIds = spotifyId
    ? spotifyId.split(",").map((id) => id.trim())
    : [];
  const facebookUrls = facebookUrl
    ? facebookUrl.split(",").map((url) => url.trim())
    : [];
  const instagramUrls = instagramUrl
    ? instagramUrl.split(",").map((url) => url.trim())
    : [];

  return names.map((name, index) => ({
    name,
    appleId: appleIds[index] || "",
    spotifyId: spotifyIds[index] || "",
    facebookUrl: facebookUrls[index] || "",
    instagramUrl: instagramUrls[index] || "",
  }));
};

const OrderDetailsPage = () => {
  const id = useParams().id;
  const userId = useSelector((state) => state.userId);
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [orderLoop, setOrderLoop] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const [audioReady, setAudioReady] = useState(false);
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  // States for parsed artists
  const [parsedSingers, setParsedSingers] = useState([]);
  const [parsedComposers, setParsedComposers] = useState([]);
  const [parsedLyricists, setParsedLyricists] = useState([]);
  // States for download payment
  const [downloadPaymentLoading, setDownloadPaymentLoading] = useState(false);
  const [downloadFileType, setDownloadFileType] = useState(null); // "thumbnail" or "audio"

  // Notification function
  const openNotificationWithIcon = (type, message) => {
    notification[type]({
      message: message,
      placement: "topRight",
    });
  };

  // Fetch order data
  const fetcher = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/order/get-order/?id=${id}`,
      );
      const data = await res.json();

      if (!data.order) {
        messageApi.error("Order not found");
        navigate("/user-panel/history");
        return;
      }

      setOrder(data.order);

      // Parse artists data
      setParsedSingers(
        parseArtists(
          data.order.singer,
          data.order.singerAppleId,
          data.order.singerSpotifyId,
          data.order.singerFacebookUrl,
          data.order.singerInstagramUrl,
        ),
      );

      setParsedComposers(
        parseArtists(
          data.order.composer,
          data.order.composerAppleId,
          data.order.composerSpotifyId,
          data.order.composerFacebookUrl,
          data.order.composerInstagramUrl,
        ),
      );

      setParsedLyricists(
        parseArtists(
          data.order.lyricist,
          data.order.lyricistAppleId,
          data.order.lyricistSpotifyId,
          data.order.lyricistFacebookUrl,
          data.order.lyricistInstagramUrl,
        ),
      );

      // Process order fields
      let arr = [];
      for (const key in data.order) {
        if (
          key === "_id" ||
          key === "id" ||
          key === "userId" ||
          key === "deleted" ||
          key === "__v"
        ) {
          continue;
        }
        if (data.order[key].length === 0) {
          continue;
        }

        const obj = {
          field: key,
          value: data.order[key],
          id: Math.random() * 99999999999,
        };
        arr.push(obj);
      }
      setOrderLoop(arr);
    } catch (error) {
      console.error("Error fetching order:", error);
      messageApi.error("Failed to load order details");
    } finally {
      setIsLoading(false);
    }
  };

  // Delete order confirmation
  const confirm = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/order/update-order/?id=${order.id}&action=delete`,
        {
          method: "PATCH",
        },
      );

      if (res.ok) {
        messageApi.success("Order deleted successfully");
        setTimeout(() => {
          navigate("/user-panel/history");
        }, 1000);
      } else {
        messageApi.error("Failed to delete order");
      }
    } catch (error) {
      console.error("Error deleting order:", error);
      messageApi.error("An error occurred");
    }
  };

  // Takedown order confirmation
  const handleTakedown = async () => {
    // Load Razorpay script first
    const razorpayLoaded = await loadRazorpayScript(
      "https://checkout.razorpay.com/v1/checkout.js",
    );

    if (!razorpayLoaded) {
      messageApi.error(
        "Razorpay SDK failed to load. Please check your internet connection.",
      );
      return;
    }

    try {
      // Create payment order for takedown
      const orderRes = await fetch(
        `${process.env.REACT_APP_BASE_URL}/payment/create-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount: 5, // Takedown fee amount (₹5.00)
            currency: "INR",
          }),
        },
      );

      const paymentData = await orderRes.json();
      console.log("Payment order created:", paymentData);

      if (!paymentData || !paymentData.order_id) {
        messageApi.error("Failed to create payment order. Please try again.");
        return;
      }

      // Razorpay options
      const options = {
        key: "rzp_live_TG53Pr9EPozggs", // Replace with your Razorpay test/live key
        amount: paymentData.amount,
        currency: paymentData.currency,
        order_id: paymentData.order_id,
        name: "Music Distribution - Takedown",
        description: "Takedown request processing fee",
        handler: async function (response) {
          console.log("Payment Success:", response);

          try {
            // Process takedown directly after successful payment
            const res = await fetch(
              `${process.env.REACT_APP_BASE_URL}/order/update-order/?id=${
                order.id
              }&action=takedown&userId=${"664b0a564ea2493604d28fb8"}`,
              {
                method: "PATCH",
              },
            );
            const data = await res.json();
            console.log(data);

            if (res.ok) {
              openNotificationWithIcon("success", data.message);
              setTimeout(() => {
                navigate("/user-panel/history");
              }, 500);
            } else {
              messageApi.error("Failed to take down album");
            }
          } catch (error) {
            console.error("Error processing takedown after payment:", error);
            messageApi.error(
              "Payment successful but failed to process takedown. Please contact support.",
            );
          }
        },
        prefill: {
          name: order.artistName || "User",
          email: order.email || "",
          contact: order.phone || "",
        },
        theme: {
          color: "#fa8c16",
        },
        modal: {
          ondismiss: function () {
            messageApi.info(
              "Payment cancelled. Takedown request not processed.",
            );
          },
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error("Error initiating takedown payment:", error);
      messageApi.error("Failed to initiate payment. Please try again.");
    }
  };

  // Load Razorpay script
  const loadRazorpayScript = (src) => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  // Handle download with payment
  const handleDownloadPayment = async (fileType) => {
    setDownloadFileType(fileType);
    setDownloadPaymentLoading(true);

    // Load Razorpay script
    const res = await loadRazorpayScript(
      "https://checkout.razorpay.com/v1/checkout.js",
    );

    if (!res) {
      messageApi.error(
        "Razorpay SDK failed to load. Please check your internet connection.",
      );
      setDownloadPaymentLoading(false);
      return;
    }

    try {
      // Create payment order for download (₹5 = 500 paise)
      const orderRes = await fetch(
        `${process.env.REACT_APP_BASE_URL}/payment/create-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount: 5, // ₹5 in paise
            currency: "INR",
          }),
        },
      );

      const paymentData = await orderRes.json();
      console.log("Download payment order created:", paymentData);

      if (!paymentData || !paymentData.order_id) {
        messageApi.error("Failed to create payment order. Please try again.");
        setDownloadPaymentLoading(false);
        return;
      }

      // Determine file URL and name
      const fileUrl =
        fileType === "thumbnail"
          ? order.thumbnail
          : fileType === "moviePoster"
            ? order.moviePoster
            : order.file;
      const fileName =
        fileType === "thumbnail"
          ? `${order.title || order.songtitle || "cover"}-art`
          : fileType === "moviePoster"
            ? `${order.title || order.songtitle || "movie-poster"}`
            : `${order.title || order.songtitle || "audio"}`;
      const downloadLink = `${process.env.REACT_APP_BASE_URL}/file/download/?filePath=${fileUrl}&title=${fileName}`;

      // Razorpay options
      const options = {
        key: "rzp_live_TG53Pr9EPozggs", // Replace with your Razorpay test/live key
        amount: paymentData.amount,
        currency: paymentData.currency,
        order_id: paymentData.order_id,
        name: "Music Distribution - Download",
        description: `Download ${
          fileType === "thumbnail"
            ? "Cover Art"
            : fileType === "moviePoster"
              ? "Movie Poster"
              : "Audio File"
        }`,
        handler: async function (response) {
          // Direct navigation to download URL (bypasses popup blocker)
          window.location.href = downloadLink;
          messageApi.success("Download starting!");
          setDownloadPaymentLoading(false);
          setDownloadFileType(null);
        },
        prefill: {
          name: order.artistName || "User",
          email: order.email || "",
          contact: order.phone || "",
        },
        theme: {
          color: "#32a86d",
        },
        modal: {
          ondismiss: function () {
            messageApi.info("Payment cancelled. Download not completed.");
            setDownloadPaymentLoading(false);
            setDownloadFileType(null);
          },
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error("Error initiating download payment:", error);
      messageApi.error("Failed to initiate payment. Please try again.");
      setDownloadPaymentLoading(false);
      setDownloadFileType(null);
    }
  };

  // Copy to clipboard functionality
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      messageApi.success("Copied to clipboard");
    } catch (err) {
      console.error("Failed to copy:", err);
      messageApi.error("Failed to copy text");
    }
  };

  useEffect(() => {
    fetcher();
  }, [id]);

  // Format field name for display
  const formatFieldName = (field) => {
    // Handle special cases
    if (field === "labelName") return "Label Name";
    if (field === "songtitle") return "Song Title";
    if (field === "title") return "Album Title";
    if (field === "subLabel1" || field === "subLabel2" || field === "subLabel3")
      return "Sub Label";
    if (field === "dateOfRelease") return "Scheduled Release Date";
    if (field === "dateLive") return "Date Live";
    if (field === "releaseDate") return "Previous Release Date";
    if (field === "AlbumType" || field === "albumType") return "Album Type";
    if (field === "orderDateAndTime") return "Order Date";
    if (field === "musicDirector") return "Music Director";
    if (field === "starCast") return "Star Cast";

    // General formatting
    return field
      .replace(/([A-Z])/g, " $1") // Insert space before capital letters
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
      .trim();
  };
  // Render field value based on field type
  const renderFieldValue = (field, value) => {
    const fieldLower = field.toLowerCase();

    // Handle YouTube Content ID and YouTube Music fields specifically
    if (fieldLower === "youtube content id" || fieldLower === "youtube music") {
      return (
        <>
          <span>{value}</span>
          <Tooltip title="Copy to clipboard">
            <Button
              type="text"
              size="small"
              icon={<FaCopy size={14} />}
              onClick={() => copyToClipboard(value)}
            />
          </Tooltip>
        </>
      );
    }

    // URLs and external links
    if (
      (fieldLower.includes("url") || fieldLower.includes("id")) &&
      !fieldLower.includes("_id") &&
      !fieldLower.includes("userid") &&
      !fieldLower.includes("youtube")
    ) {
      return (
        <Link to={value} target="_blank">
          {getSocialIcon(field)} View on{" "}
          {fieldLower.includes("apple")
            ? "Apple Music"
            : fieldLower.includes("facebook")
              ? "Facebook"
              : fieldLower.includes("instagram")
                ? "Instagram"
                : fieldLower.includes("spotify")
                  ? "Spotify"
                  : "Platform"}
        </Link>
      );
    }

    // File downloads
    if (field === "thumbnail" || field === "file" || field === "moviePoster") {
      const label =
        field === "thumbnail"
          ? "Cover Art"
          : field === "moviePoster"
            ? "Movie Poster"
            : "Audio File";
      return (
        <Link
          to={`${process.env.REACT_APP_BASE_URL}/file/download/?filePath=${value}`}
          target="_blank"
        >
          <FaDownload /> Download {label}
        </Link>
      );
    }

    // Status field
    if (fieldLower === "status") {
      const statusInfo = getStatusInfo(value);
      return (
        <Tag icon={statusInfo.icon} color={statusInfo.color}>
          {statusInfo.text}
        </Tag>
      );
    }

    // Default text with copy option
    if (fieldLower === "lyrics") {
      return (
        <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {value}
        </span>
      );
    }

    return (
      <>
        <span>{value === "completed" ? "Live" : value}</span>
        <Tooltip title="Copy to clipboard">
          <Button
            type="text"
            size="small"
            icon={<FaCopy size={14} />}
            onClick={() => copyToClipboard(value)}
          />
        </Tooltip>
      </>
    );
  };

  if (isLoading) {
    return (
      <PageContainer>
        {contextHolder}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh",
          }}
        >
          <MusicLoader />
        </div>
      </PageContainer>
    );
  }

  // Group fields for better organization
  const groupedFields = groupFields(orderLoop);

  // Get status information
  const statusInfo = order && order.status ? getStatusInfo(order.status) : null;
  const descriptionText = order?.description || "";
  const hasLongDescription = descriptionText.length > 50;
  const descriptionPreview = hasLongDescription
    ? `${descriptionText.slice(0, 50)}...`
    : descriptionText;

  return (
    <PageContainer>
      {contextHolder}

      {/* Page header */}
      <PageHeader>
        <div>
          <BreadcrumbContainer>
            <Breadcrumb
              items={[
                { title: <Link to="/user-panel">Dashboard</Link> },
                { title: <Link to="/user-panel/history">My Albums</Link> },
                { title: "Album Details" },
              ]}
            />
          </BreadcrumbContainer>

          <NavigationRow>
            <Space>
              <Title level={2} style={{ margin: 0 }}>
                Album Details
              </Title>
              {statusInfo && (
                <StatusTag icon={statusInfo.icon} color={statusInfo.color}>
                  {statusInfo.text}
                </StatusTag>
              )}
            </Space>

            <BackButton
              onClick={() => navigate("/user-panel/history")}
              icon={<FaArrowLeft />}
            >
              Back to Albums
            </BackButton>
          </NavigationRow>
        </div>
      </PageHeader>

      {/* Main content */}
      {order && (
        <ContentCard>
          {/* Album section (left side) */}
          <AlbumSection>
            <AlbumCover>
              <Image
                width={240}
                src={
                  order.thumbnail.includes("cloudinary")
                    ? order.thumbnail
                    : `${process.env.REACT_APP_BASE_URL}/${order.thumbnail}`
                }
                placeholder={
                  <Image
                    preview={false}
                    src={
                      order.thumbnail.includes("cloudinary")
                        ? order.thumbnail
                        : `${process.env.REACT_APP_BASE_URL}/${order.thumbnail}`
                    }
                    width={240}
                  />
                }
              />
            </AlbumCover>

            <AlbumTitle level={3}>
              {order.songtitle ? order.songtitle : order.title}
            </AlbumTitle>
            <StyledAudioPlayer>
              {!audioReady ? (
                <Button
                  type="primary"
                  icon={<FaPlayCircle />}
                  size="large"
                  onClick={() => setAudioReady(true)}
                  style={{
                    width: "100%",
                    height: "48px",
                    marginBottom: "8px",
                    background: "linear-gradient(to right, #1677ff, #69c0ff)",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    fontSize: "16px",
                    transition: "all 0.3s ease",
                  }}
                >
                  Play Audio
                </Button>
              ) : (
                <AudioPlayer
                  key="audio-player"
                  src={`${process.env.REACT_APP_BASE_URL}/${order.file}`}
                  autoPlayAfterSrcChange={true}
                  showJumpControls={false}
                  customAdditionalControls={[]}
                  layout="stacked-reverse"
                />
              )}
            </StyledAudioPlayer>

            {order.description && (
              <>
                <AlbumDescription
                  $clickable={hasLongDescription}
                  onClick={() => {
                    if (hasLongDescription) setIsDescriptionModalOpen(true);
                  }}
                >
                  {descriptionPreview}
                </AlbumDescription>
                {hasLongDescription && (
                  <DescriptionButton
                    type="link"
                    onClick={() => setIsDescriptionModalOpen(true)}
                  >
                    Show full description
                  </DescriptionButton>
                )}
              </>
            )}
          </AlbumSection>

          {/* Details section (right side) */}
          <DetailsSection>
            <SectionTitle level={4}>Album Information</SectionTitle>

            {/* Main information group */}
            {groupedFields.main.length > 0 && (
              <DetailsGroup>
                <GroupTitle level={5}>
                  <FaInfoCircle /> Basic Information
                </GroupTitle>
                <DetailsList>
                  {groupedFields.labels.map(({ field, value, id }) => {
                    const formattedField = formatFieldName(field);

                    return (
                      <DetailRow key={id}>
                        <DetailLabel>
                          {getFieldIcon(formattedField)}
                          {formattedField}
                        </DetailLabel>
                        <DetailValue>
                          {renderFieldValue(formattedField, value)}
                        </DetailValue>
                      </DetailRow>
                    );
                  })}
                  {groupedFields.main.map(({ field, value, id }) => {
                    // Skip fields already displayed in the left panel
                    if (field === "title" || field === "description")
                      return null;

                    const formattedField = formatFieldName(field);

                    return (
                      <DetailRow
                        key={id}
                        highlighted={field.toLowerCase() === "status"}
                      >
                        <DetailLabel>
                          {getFieldIcon(formattedField)}
                          {formattedField}
                        </DetailLabel>
                        <DetailValue>
                          {renderFieldValue(formattedField, value)}
                        </DetailValue>
                      </DetailRow>
                    );
                  })}
                  {groupedFields.other.map(({ field, value, id }) => {
                    // Skip artist fields as they're handled separately
                    if (
                      field === "file" ||
                      field === "thumbnail" ||
                      field === "moviePoster" ||
                      field.includes("singer") ||
                      field.includes("composer") ||
                      field.includes("lyricist") ||
                      field === "musicDirector" ||
                      field === "director" ||
                      field === "producer" ||
                      field === "starCast"
                    )
                      return null;

                    const formattedField = formatFieldName(field);

                    return (
                      <DetailRow key={id}>
                        <DetailLabel>
                          {getFieldIcon(formattedField)}
                          {formattedField}
                        </DetailLabel>
                        <DetailValue>
                          {renderFieldValue(formattedField, value)}
                        </DetailValue>
                      </DetailRow>
                    );
                  })}
                </DetailsList>
              </DetailsGroup>
            )}

            {/* Dates group */}
            {groupedFields.dates.length > 0 && (
              <DetailsGroup>
                <GroupTitle level={5}>
                  <FaCalendarAlt /> Dates
                </GroupTitle>
                <DetailsList>
                  {groupedFields.dates.map(({ field, value, id }) => {
                    const formattedField = formatFieldName(field);
                    let displayValue = value;

                    // Special handling for orderDateAndTime to extract just the date part
                    if (field === "orderDateAndTime") {
                      displayValue = value.split("/")[0];
                    }

                    return (
                      <DetailRow key={id}>
                        <DetailLabel>
                          {getFieldIcon(formattedField)}
                          {formattedField}
                        </DetailLabel>
                        <DetailValue>
                          {renderFieldValue(formattedField, displayValue)}
                        </DetailValue>
                      </DetailRow>
                    );
                  })}
                </DetailsList>
              </DetailsGroup>
            )}

            {/* Artists section */}
            <DetailsGroup>
              <GroupTitle level={5}>
                <FaUser /> Artists
              </GroupTitle>

              {/* Singers */}
              {parsedSingers.length > 0 && (
                <>
                  <Text
                    type="secondary"
                    style={{ marginBottom: "8px", display: "block" }}
                  >
                    Singer
                  </Text>
                  <div style={{ marginBottom: "16px" }}>
                    {parsedSingers.map((singer, idx) => (
                      <ArtistTag key={idx}>
                        <span
                          style={{
                            fontWeight: "500",
                            fontSize: ".8rem",
                            marginRight: "auto",
                            color: "black",
                            letterSpacing: ".06rem",
                          }}
                        >
                          {singer.name}
                        </span>
                        <SocialLinks>
                          <SocialIcon
                            active={singer.facebookUrl?.trim().length > 0}
                            onClick={() =>
                              singer.facebookUrl &&
                              window.open(singer.facebookUrl, "_blank")
                            }
                            title={
                              singer.facebookUrl
                                ? "Visit Facebook profile"
                                : "No Facebook profile"
                            }
                          >
                            <FaFacebook />
                          </SocialIcon>
                          <SocialIcon
                            active={singer.instagramUrl?.trim().length > 0}
                            onClick={() =>
                              singer.instagramUrl &&
                              window.open(singer.instagramUrl, "_blank")
                            }
                            title={
                              singer.instagramUrl
                                ? "Visit Instagram profile"
                                : "No Instagram profile"
                            }
                          >
                            <FaInstagram />
                          </SocialIcon>
                          <SocialIcon
                            active={singer.appleId?.trim().length > 0}
                            onClick={() =>
                              singer.appleId &&
                              window.open(singer.appleId, "_blank")
                            }
                            title={
                              singer.appleId
                                ? "Visit Apple Music profile"
                                : "No Apple Music profile"
                            }
                          >
                            <FaApple />
                          </SocialIcon>
                          <SocialIcon
                            active={singer.spotifyId?.trim().length > 0}
                            onClick={() =>
                              singer.spotifyId &&
                              window.open(singer.spotifyId, "_blank")
                            }
                            title={
                              singer.spotifyId
                                ? "Visit Spotify profile"
                                : "No Spotify profile"
                            }
                          >
                            <FaSpotify style={{ fontSize: "1.1rem" }} />
                          </SocialIcon>
                        </SocialLinks>
                      </ArtistTag>
                    ))}
                  </div>
                </>
              )}

              {/* Composers */}
              {parsedComposers.length > 0 && (
                <>
                  <Text
                    type="secondary"
                    style={{ margin: "16px 0 8px", display: "block" }}
                  >
                    Composer
                  </Text>
                  <div style={{ marginBottom: "16px" }}>
                    {parsedComposers.map((composer, idx) => (
                      <ArtistTag key={idx}>
                        <span
                          style={{
                            fontWeight: "500",
                            fontSize: ".8rem",
                            marginRight: "auto",
                            color: "black",
                            letterSpacing: ".06rem",
                          }}
                        >
                          {composer.name}
                        </span>
                        <SocialLinks>
                          <SocialIcon
                            active={composer.facebookUrl?.trim().length > 0}
                            onClick={() =>
                              composer.facebookUrl &&
                              window.open(composer.facebookUrl, "_blank")
                            }
                            title={
                              composer.facebookUrl
                                ? "Visit Facebook profile"
                                : "No Facebook profile"
                            }
                          >
                            <FaFacebook />
                          </SocialIcon>
                          <SocialIcon
                            active={composer.instagramUrl?.trim().length > 0}
                            onClick={() =>
                              composer.instagramUrl &&
                              window.open(composer.instagramUrl, "_blank")
                            }
                            title={
                              composer.instagramUrl
                                ? "Visit Instagram profile"
                                : "No Instagram profile"
                            }
                          >
                            <FaInstagram />
                          </SocialIcon>
                          <SocialIcon
                            active={composer.appleId?.trim().length > 0}
                            onClick={() =>
                              composer.appleId &&
                              window.open(composer.appleId, "_blank")
                            }
                            title={
                              composer.appleId
                                ? "Visit Apple Music profile"
                                : "No Apple Music profile"
                            }
                          >
                            <FaApple />
                          </SocialIcon>
                          <SocialIcon
                            active={composer.spotifyId?.trim().length > 0}
                            onClick={() =>
                              composer.spotifyId &&
                              window.open(composer.spotifyId, "_blank")
                            }
                            title={
                              composer.spotifyId
                                ? "Visit Spotify profile"
                                : "No Spotify profile"
                            }
                          >
                            <FaSpotify style={{ fontSize: "1.1rem" }} />
                          </SocialIcon>
                        </SocialLinks>
                      </ArtistTag>
                    ))}
                  </div>
                </>
              )}

              {/* Lyricists */}
              {parsedLyricists.length > 0 && (
                <>
                  <Text
                    type="secondary"
                    style={{ margin: "16px 0 8px", display: "block" }}
                  >
                    Lyricist
                  </Text>
                  <div style={{ marginBottom: "16px" }}>
                    {parsedLyricists.map((lyricist, idx) => (
                      <ArtistTag key={idx}>
                        <span
                          style={{
                            fontWeight: "500",
                            fontSize: ".8rem",
                            marginRight: "auto",
                            color: "black",
                            letterSpacing: ".06rem",
                          }}
                        >
                          {lyricist.name}
                        </span>
                        <SocialLinks>
                          <SocialIcon
                            active={lyricist.facebookUrl?.trim().length > 0}
                            onClick={() =>
                              lyricist.facebookUrl &&
                              window.open(lyricist.facebookUrl, "_blank")
                            }
                            title={
                              lyricist.facebookUrl
                                ? "Visit Facebook profile"
                                : "No Facebook profile"
                            }
                          >
                            <FaFacebook />
                          </SocialIcon>
                          <SocialIcon
                            active={lyricist.instagramUrl?.trim().length > 0}
                            onClick={() =>
                              lyricist.instagramUrl &&
                              window.open(lyricist.instagramUrl, "_blank")
                            }
                            title={
                              lyricist.instagramUrl
                                ? "Visit Instagram profile"
                                : "No Instagram profile"
                            }
                          >
                            <FaInstagram />
                          </SocialIcon>
                          <SocialIcon
                            active={lyricist.appleId?.trim().length > 0}
                            onClick={() =>
                              lyricist.appleId &&
                              window.open(lyricist.appleId, "_blank")
                            }
                            title={
                              lyricist.appleId
                                ? "Visit Apple Music profile"
                                : "No Apple Music profile"
                            }
                          >
                            <FaApple />
                          </SocialIcon>
                          <SocialIcon
                            active={lyricist.spotifyId?.trim().length > 0}
                            onClick={() =>
                              lyricist.spotifyId &&
                              window.open(lyricist.spotifyId, "_blank")
                            }
                            title={
                              lyricist.spotifyId
                                ? "Visit Spotify profile"
                                : "No Spotify profile"
                            }
                          >
                            <FaSpotify style={{ fontSize: "1.1rem" }} />
                          </SocialIcon>
                        </SocialLinks>
                      </ArtistTag>
                    ))}
                  </div>
                </>
              )}

              {/* Other artist fields in regular format */}
              {order.musicDirector && (
                <DetailRow>
                  <DetailLabel>
                    <FaMusic />
                    Music Director
                  </DetailLabel>
                  <DetailValue>{order.musicDirector}</DetailValue>
                </DetailRow>
              )}

              {order.director && (
                <DetailRow>
                  <DetailLabel>
                    <FaUsers />
                    Director
                  </DetailLabel>
                  <DetailValue>{order.director}</DetailValue>
                </DetailRow>
              )}

              {order.producer && (
                <DetailRow>
                  <DetailLabel>
                    <FaUsers />
                    Producer
                  </DetailLabel>
                  <DetailValue>{order.producer}</DetailValue>
                </DetailRow>
              )}

              {order.starCast && (
                <DetailRow>
                  <DetailLabel>
                    <FaUsers />
                    Star Cast
                  </DetailLabel>
                  <DetailValue>{order.starCast}</DetailValue>
                </DetailRow>
              )}
            </DetailsGroup>

            {/* Downloads section */}
            <DetailsGroup>
              <GroupTitle level={5}>
                <FaCloudDownloadAlt /> Downloads
              </GroupTitle>
              <DetailsList>
                <DetailRow>
                  <DetailLabel>
                    <FaCloudDownloadAlt />
                    Cover Art
                  </DetailLabel>
                  <DetailValue>
                    <Button
                      type="primary"
                      icon={<FaDownload />}
                      onClick={() => handleDownloadPayment("thumbnail")}
                      loading={
                        downloadPaymentLoading &&
                        downloadFileType === "thumbnail"
                      }
                      disabled={downloadPaymentLoading}
                    >
                      Download Image (₹5)
                    </Button>
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>
                    <MdMusicNote size={16} style={{ marginRight: "8px" }} />
                    Audio File
                  </DetailLabel>
                  <DetailValue>
                    <Button
                      type="primary"
                      icon={<FaDownload />}
                      onClick={() => handleDownloadPayment("audio")}
                      loading={
                        downloadPaymentLoading && downloadFileType === "audio"
                      }
                      disabled={downloadPaymentLoading}
                    >
                      Download Audio (₹5)
                    </Button>
                  </DetailValue>
                </DetailRow>
                {order.moviePoster && (
                  <DetailRow>
                    <DetailLabel>
                      <MdImage size={16} style={{ marginRight: "8px" }} />
                      Movie Poster
                    </DetailLabel>
                    <DetailValue>
                      <Button
                        type="primary"
                        icon={<FaDownload />}
                        onClick={() => handleDownloadPayment("moviePoster")}
                        loading={
                          downloadPaymentLoading &&
                          downloadFileType === "moviePoster"
                        }
                        disabled={downloadPaymentLoading}
                      >
                        Download Movie Poster (₹5)
                      </Button>
                    </DetailValue>
                  </DetailRow>
                )}
              </DetailsList>
            </DetailsGroup>

            {/* Action buttons */}
            {(order.status === "waiting" || order.status === "rejected") &&
              order.deleted === false && (
                <ActionButtons>
                  <Popconfirm
                    title="Delete this album?"
                    description="This action cannot be undone."
                    onConfirm={confirm}
                    okText="Yes, Delete"
                    cancelText="Cancel"
                    placement="top"
                  >
                    <DeleteButton>
                      <FaTrash /> Delete
                    </DeleteButton>
                  </Popconfirm>

                  <EditButton to={`/user-panel/order/${order._id}/edit`}>
                    <FaEdit /> Edit
                  </EditButton>
                </ActionButtons>
              )}

            {/* Takedown button for completed orders */}
            {order.status === "completed" && order.deleted === false && (
              <ActionButtons>
                <Popconfirm
                  title="Request takedown for this album?"
                  description="This will remove your music from all platforms. A processing fee will be charged."
                  onConfirm={handleTakedown}
                  okText="Pay"
                  cancelText="Cancel"
                  placement="top"
                >
                  <TakedownButton>
                    <FaTimesCircle /> Request Takedown
                  </TakedownButton>
                </Popconfirm>
              </ActionButtons>
            )}
          </DetailsSection>
        </ContentCard>
      )}

      <Modal
        title="Full Description"
        open={isDescriptionModalOpen}
        onCancel={() => setIsDescriptionModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsDescriptionModalOpen(false)}>
            Close
          </Button>,
        ]}
      >
        <FullDescription>{descriptionText}</FullDescription>
      </Modal>
    </PageContainer>
  );
};

export default OrderDetailsPage;
